import { useState, useEffect } from "react";
import { Icon, CatIcon } from "../shared/Icons";
import { DarkToggle }    from "../shared/DarkToggle";
import { StatusBadge }   from "../shared/StatusBadge";
import { Toast }         from "../shared/Toast";
import { ProductModal }  from "./ProductModal";
import { AdminSettings } from "./AdminSettings";
import { api }           from "../../api";

export function AdminDashboard({ user, products, orders, onLogout, onUpdateProducts, onUpdateOrders, dark, setDark }) {
  const [tab, setTab]               = useState("dashboard");
  const [showModal, setShowModal]   = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch]         = useState("");
  const [toast, setToast]           = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers]           = useState([]);

  const closeNav   = t => { setTab(t); setSidebarOpen(false); };
  const showToast  = msg => { setToast(msg); setTimeout(() => setToast(""), 3200); };
  
  const fDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date)) return d;
    return date.toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });
  };

  // Fetch users when on users tab
  useEffect(() => {
    if (tab === "users") {
      api("/api/users").then(setUsers).catch(() => {});
    }
  }, [tab]);

  const totalRevenue = orders.reduce((s, o) => s + o.total, 0);
  const pending      = orders.filter(o => o.status === "Pending").length;
  const lowStock     = products.filter(p => p.qty < 10).length;
  const filtered     = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  const saveProduct = async form => {
    try {
      if (editProduct) {
        await api(`/api/products/${editProduct.id}`, { method:"PUT", body:{ ...form, price:+form.price, qty:+form.qty }});
        showToast("Product updated");
      } else {
        await api("/api/products", { method:"POST", body:{ ...form, price:+form.price, qty:+form.qty }});
        showToast("Product added");
      }
      // Directly refetch and update parent with fresh data from DB
      const updated = await api("/api/products", { auth:false });
      onUpdateProducts(updated);
    } catch { showToast("Failed to save product"); }
    setShowModal(false); setEditProduct(null);
  };

  const deleteProduct = async id => {
    try {
      await api(`/api/products/${id}`, { method:"DELETE" });
      const updated = await api("/api/products", { auth:false });
      onUpdateProducts(updated);
      showToast("Product removed");
    } catch { showToast("Failed to delete product"); }
  };

  const updateStatus = async (id, st) => {
    try {
      await api(`/api/orders/${id}/status`, { method:"PUT", body:{ status:st }});
      const updated = await api("/api/orders");
      onUpdateOrders(updated);
      showToast(`Order marked as ${st}`);
    } catch { showToast("Failed to update order"); }
  };

  const deleteUser = async id => {
    try {
      await api(`/api/users/${id}`, { method:"DELETE" });
      setUsers(u => u.filter(x => x.id !== id));
      showToast("User removed");
    } catch (err) { showToast(err.message || "Failed"); }
  };

  const navItems = [
    { id:"dashboard", icon:"dashboard", label:"Dashboard" },
    { id:"products",  icon:"products",  label:"Products"  },
    { id:"orders",    icon:"orders",    label:"Orders"    },
    { id:"inventory", icon:"inventory", label:"Inventory" },
    { id:"users",     icon:"admin",     label:"Users"     },
    { id:"settings",  icon:"settings",  label:"Settings"  },
  ];

  return (
    <div className="admin-app page-enter">
      {/* MOBILE TOP BAR */}
      <div className="admin-mobile-topbar">
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div className="logo-mark"><img src="/logo.png" alt="K Dental" /></div>
          <span className="logo-text" style={{ fontSize:18 }}>K <span>Dental</span></span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <DarkToggle dark={dark} setDark={setDark}/>
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">
            <span/><span/><span/>
          </button>
        </div>
      </div>

      <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={() => setSidebarOpen(false)}/>

      <aside className={`sidebar ${sidebarOpen?"open":""}`}>
        <div className="sb-brand">
          <div className="logo-mark"><img src="/logo.png" alt="K Dental" /></div>
          <span className="logo-text" style={{ fontSize:18 }}>K <span>Dental</span></span>
        </div>
        <nav className="sb-nav">
          <div className="sb-section">Menu</div>
          {navItems.map(n => (
            <button key={n.id} className={`nav-item ${tab===n.id?"active":""}`} onClick={() => closeNav(n.id)}>
              <Icon name={n.icon} size={15}/> {n.label}
            </button>
          ))}
        </nav>
        <div className="sb-footer">
          <div style={{ marginBottom:12 }}><DarkToggle dark={dark} setDark={setDark}/></div>
          <div className="sb-user"><strong>{user.name}</strong>Administrator</div>
          <button className="btn-logout" onClick={onLogout}><Icon name="logout" size={13}/> Sign Out</button>
        </div>
      </aside>

      <main className="main-content">

        {/* ── DASHBOARD ── */}
        {tab === "dashboard" && <>
          <div className="page-header page-enter">
            <div><h1>Dashboard</h1><p>Welcome back, {user.name}</p></div>
          </div>
          <div className="stats-grid">
            {[
              { icon: "revenue", val: `₹${(totalRevenue/100000).toFixed(1)}L`, label: "Total Revenue", trend: <><Icon name="trending" size={12}/> All time</>, cls: "" },
              { icon: "products", val: products.length, label: "Total Products", trend: "In catalogue", cls: "" },
              { icon: "orders", val: orders.length, label: "Total Orders", trend: `${pending} pending`, cls: "" },
              { icon: "warning", val: lowStock, label: "Low Stock Items", trend: <><Icon name="warning" size={12}/> Needs attention</>, cls: "stat-icon-warn", trendCls: "stat-warn-text" },
            ].map((s, i) => (
              <div className="stat-card page-enter" key={s.label} style={{ animationDelay: `${i * 80}ms` }}>
                <div className={`stat-icon-wrap ${s.cls}`}><Icon name={s.icon} size={18}/></div>
                <div className="stat-val">{s.val}</div>
                <div className="stat-label">{s.label}</div>
                <div className={`stat-trend ${s.trendCls || ""}`}>{s.trend}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Recent Orders</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setTab("orders")}>View All <Icon name="arrowRight" size={12}/></button>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Order ID</th><th>Customer</th><th>Total</th><th>Payment</th><th>Reg. No.</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {orders.slice(0,5).map(o => (
                    <tr key={o.id}>
                      <td><strong>{o.id}</strong></td>
                      <td>{o.user_name || o.userName}</td>
                      <td><strong>₹{o.total.toLocaleString()}</strong></td>
                      <td style={{ color:"var(--text3)" }}>{o.payment}</td>
                      <td style={{ fontSize:12, color:"var(--text2)" }}>{o.regNumber || "—"}</td>
                      <td><StatusBadge status={o.status}/></td>
                      <td style={{ color:"var(--text3)" }}>{fDate(o.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Product Overview</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th></tr></thead>
                <tbody>
                  {products.slice(0,6).map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:34,height:34,background:"var(--bg2)",borderRadius:8,display:"grid",placeItems:"center",color:"var(--blue)",flexShrink:0,overflow:"hidden",border:"1px solid var(--border)" }}>
                            {p.image ? <img src={p.image} alt={p.name} style={{ width:"100%",height:"100%",objectFit:"contain",padding:2 }}/> : <CatIcon category={p.category} size={16}/>}
                          </div>
                          <strong style={{ fontSize:13 }}>{p.name}</strong>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{p.category}</span></td>
                      <td><strong>₹{p.price.toLocaleString()}</strong></td>
                      <td><span className={`badge ${p.qty<10?"badge-red":p.qty<30?"badge-amber":"badge-green"}`}>{p.qty} units</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>}

        {/* ── PRODUCTS ── */}
        {tab === "products" && <>
          <div className="page-header">
            <div><h1>Product Management</h1><p>Add, edit and manage your catalogue</p></div>
          </div>
          <div style={{ display:"flex", gap:12, marginBottom:18, flexWrap:"wrap" }}>
            <div className="search-bar" style={{ flex:1, minWidth:0, maxWidth:380 }}>
              <span style={{ color:"var(--text3)",display:"flex",alignItems:"center" }}><Icon name="search" size={14}/></span>
              <input placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
            <button className="btn btn-primary" onClick={() => { setEditProduct(null); setShowModal(true); }}>
              <Icon name="plus" size={14}/> Add Product
            </button>
          </div>
          {filtered.length === 0 ? (
            <div className="empty"><div className="empty-icon"><Icon name="search" size={40}/></div><h3>No Products Found</h3><p>Try a different search</p></div>
          ) : (
            <div className="products-grid">
              {filtered.map(p => (
                <div className="product-admin-card" key={p.id}>
                  <div className="product-img-admin">
                    {p.image ? <img src={p.image} alt={p.name} className="product-img-photo"/> : <CatIcon category={p.category} size={36}/>}
                  </div>
                  <div className="product-admin-info">
                    <div className="p-admin-cat">{p.category}</div>
                    <div className="p-admin-name">{p.name}</div>
                    <div className="p-admin-price">₹{p.price.toLocaleString()}</div>
                    <div className="p-admin-qty">{p.qty} units available</div>
                    <div className="product-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditProduct(p); setShowModal(true); }}><Icon name="edit" size={12}/> Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteProduct(p.id)}><Icon name="trash" size={12}/> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>}

        {/* ── ORDERS ── */}
        {tab === "orders" && <>
          <div className="page-header"><div><h1>Order Management</h1><p>View and manage all customer orders</p></div></div>
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Order ID</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Reg. No.</th><th>Address</th><th>Status</th><th>Update</th></tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td><strong>{o.id}</strong><br/><span style={{ color:"var(--text3)",fontSize:11 }}>{fDate(o.date)}</span></td>
                      <td><strong style={{ fontSize:13 }}>{o.user_name || o.userName}</strong></td>
                      <td style={{ maxWidth:160,fontSize:12,color:"var(--text2)" }}>{o.items.map(i=>`${i.name} ×${i.qty}`).join(", ")}</td>
                      <td><strong>₹{o.total.toLocaleString()}</strong></td>
                      <td style={{ color:"var(--text3)" }}>{o.payment}</td>
                      <td style={{ fontSize:12, color:"var(--text2)" }}>{o.regNumber || "—"}</td>
                      <td style={{ maxWidth:140,fontSize:12,color:"var(--text2)" }}>{o.address}</td>
                      <td><StatusBadge status={o.status}/></td>
                      <td>
                        <select className="status-sel" value={o.status} onChange={e => updateStatus(o.id, e.target.value)}>
                          {["Pending","Shipped","Delivered","Cancelled"].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>}

        {/* ── INVENTORY ── */}
        {tab === "inventory" && <>
          <div className="page-header"><div><h1>Inventory Tracking</h1><p>Monitor stock levels</p></div></div>
          {lowStock > 0 && (
            <div style={{ display:"flex",alignItems:"center",gap:9,padding:"10px 14px",background:"var(--amber-l)",border:"1px solid var(--amber)",borderRadius:8,marginBottom:18,fontSize:13,color:"var(--amber)",fontWeight:600 }}>
              <Icon name="warning" size={15}/> {lowStock} product{lowStock>1?"s":""} below 10 units — restock soon
            </div>
          )}
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th></tr></thead>
                <tbody>
                  {[...products].sort((a,b) => a.qty-b.qty).map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                          <div style={{ width:34,height:34,background:"var(--bg2)",borderRadius:8,display:"grid",placeItems:"center",color:"var(--blue)",flexShrink:0,overflow:"hidden",border:"1px solid var(--border)" }}>
                            {p.image?<img src={p.image} alt={p.name} style={{ width:"100%",height:"100%",objectFit:"contain",padding:2 }}/>:<CatIcon category={p.category} size={16}/>}
                          </div>
                          <div><strong style={{ fontSize:13 }}>{p.name}</strong><div style={{ color:"var(--text3)",fontSize:11 }}>ID: {p.id}</div></div>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{p.category}</span></td>
                      <td><strong>₹{p.price.toLocaleString()}</strong></td>
                      <td><strong>{p.qty}</strong> <span style={{ color:"var(--text3)",fontSize:11 }}>units</span></td>
                      <td><span className={`badge ${p.qty===0?"badge-red":p.qty<10?"badge-amber":"badge-green"}`}>{p.qty===0?"Out of Stock":p.qty<10?"Low Stock":"In Stock"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>}

        {/* ── USERS ── */}
        {tab === "users" && <>
          <div className="page-header"><div><h1>User Management</h1><p>All registered accounts</p></div></div>
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Clinic</th><th>City</th><th>Role</th><th>Status</th><th>Joined</th><th></th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong style={{ fontSize:13 }}>{u.name}</strong></td>
                      <td style={{ color:"var(--text2)",fontSize:12 }}>{u.email}</td>
                      <td style={{ color:"var(--text3)",fontSize:12 }}>{u.phone||"—"}</td>
                      <td style={{ fontSize:12 }}>{u.clinic||"—"}</td>
                      <td style={{ color:"var(--text3)",fontSize:12 }}>{u.city||"—"}</td>
                      <td><span className={`badge ${u.role==="admin"?"badge-blue":"badge-gray"}`}>{u.role}</span></td>
                      <td><span className={`badge ${u.verified?"badge-green":"badge-amber"}`}>{u.verified?"Verified":"Pending"}</span></td>
                      <td style={{ color:"var(--text3)",fontSize:11 }}>{fDate(u.created_at)}</td>
                      <td>
                        {u.id !== user.id && u.role !== "admin" && (
                          <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}>
                            <Icon name="trash" size={11}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>}

        {/* ── SETTINGS ── */}
        {tab === "settings" && <AdminSettings user={user} showToast={showToast}/>}

      </main>

      {showModal && (
        <ProductModal product={editProduct} onSave={saveProduct}
          onClose={() => { setShowModal(false); setEditProduct(null); }}/>
      )}
      {toast && <Toast msg={toast} onClose={() => setToast("")}/>}
    </div>
  );
}
